import PropTypes from 'prop-types'
import React from 'react'
import { Formik } from 'formik'
import { useNavigate, Link } from 'react-router-dom'
import * as Yup from 'yup'
import { Trans, useTranslation } from 'react-i18next'
import { ArrowLeft } from '@phosphor-icons/react'

import TextField from '../../FormLegacy/FormFieldsV2/TextField'
import PhoneField from '../../FormLegacy/FormFieldsV2/PhoneField'
import CheckboxField from '../../FormLegacy/FormFieldsV2/CheckboxField'
import { formValidation } from '../../FormLegacy/validation'
import api from '../../../api/api'

const UserData = ({ formState, setFormState, setStep }) => {
  const { t } = useTranslation(['auth', 'common'])
  const navigate = useNavigate()
  const validationSchema = Yup.object().shape({
    firstName: formValidation.required,
    lastName: formValidation.required,
    address: formValidation.required,
    apartment: formValidation.required,
    phone: formValidation.phone,
    terms: formValidation.terms,
  })

  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [error, setError] = React.useState(null)

  const onSubmit = (values) => {
    const newRegisterData = {
      email: formState.email,
      password: formState.password,
      firstName: values.firstName,
      lastName: values.lastName,
      phone: values.phone,
      street: values.address,
      apartment: values.apartment,
      code: parseInt(formState.communityCode),
    }

    setError(null)
    setIsSubmitting(true)

    setFormState((prev) => ({
      ...prev,
      ...values,
    }))

    api
      .createNewUser(newRegisterData)
      .then(() => {
        navigate('/register/success')
      })
      .catch((err) => {
        setError(err.message || t('auth:shared.somethingWentWrongLater'))
      })
      .finally(() => {
        setIsSubmitting(false)
      })
  }

  return (
    <Formik
      initialValues={{
        firstName: '',
        lastName: '',
        address: '',
        apartment: '',
        phone: '',
        terms: false,
      }}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {({ handleSubmit, values, handleChange, touched, errors, setFieldTouched }) => {
        return (
          <form onSubmit={handleSubmit} className="auth__form">
            <button type="button" className="auth__back-button" onClick={() => setStep('password')}>
              <ArrowLeft />
              {t('common:actions.back')}
            </button>
            <p className="auth__step-description">{t('auth:passwordSetup.description')}</p>
            <div className="auth__form__columns">
              <TextField
                type="text"
                name="firstName"
                label={t('auth:userData.firstName')}
                placeholder={t('auth:userData.firstNamePlaceholder')}
                value={values.firstName}
                onChange={handleChange}
                isError={touched.firstName && !!errors.firstName}
                onBlur={() => setFieldTouched('firstName')}
              />
              <TextField
                type="text"
                name="lastName"
                label={t('auth:userData.lastName')}
                placeholder={t('auth:userData.lastNamePlaceholder')}
                value={values.lastName}
                onChange={handleChange}
                isError={touched.lastName && !!errors.lastName}
                onBlur={() => setFieldTouched('lastName')}
              />
            </div>
            <div className="auth__form__columns">
              <div className="address">
                <TextField
                  type="text"
                  name="address"
                  label={t('auth:userData.address')}
                  placeholder={t('auth:userData.addressPlaceholder')}
                  value={values.address}
                  onChange={handleChange}
                  isError={touched.address && !!errors.address}
                  onBlur={() => setFieldTouched('address')}
                />
              </div>
              <TextField
                type="text"
                additionalClass="apartment"
                name="apartment"
                label={t('auth:userData.apartment')}
                placeholder={t('auth:userData.apartmentPlaceholder')}
                value={values.apartment}
                onChange={handleChange}
                isError={touched.apartment && !!errors.apartment}
                onBlur={() => setFieldTouched('apartment')}
              />
            </div>
            <PhoneField
              type="number"
              name="phone"
              label={t('auth:userData.phoneNumber')}
              placeholder={t('auth:userData.phonePlaceholder')}
              value={values.phone}
              onChange={handleChange}
              isError={touched.phone && !!errors.phone}
              onBlur={() => setFieldTouched('phone')}
            />
            <CheckboxField
              name="terms"
              value={values.terms}
              onChange={handleChange}
              isError={touched.terms && !!errors.terms}
              label={
                <span className="terms-label">
                  <Trans
                    t={t}
                    i18nKey="auth:userData.termsAgreement"
                    components={[
                      <a
                        key="tos"
                        target="_blank"
                        rel="noreferrer"
                        href="/resources/Terms_of_Service_2019.pdf"
                        onClick={(e) => e.stopPropagation()}
                      />,
                      <a
                        key="privacy"
                        target="_blank"
                        rel="noreferrer"
                        href="https://www.elevatedliving.com/privacy-policy"
                        onClick={(e) => e.stopPropagation()}
                      />,
                    ]}
                  />
                </span>
              }
            />
            {error && <p className="auth__form__description error">{error}</p>}
            <button disabled={isSubmitting} type="submit" className="button button-primary">
              {t('auth:userData.createAccount')}
            </button>
            <p className="auth__form__existing-account">
              {t('auth:passwordSetup.existingAccount')}{' '}
              <Link className="link" to="/login">
                {t('auth:passwordSetup.signIn')}
              </Link>
            </p>
          </form>
        )
      }}
    </Formik>
  )
}

UserData.propTypes = {
  formState: PropTypes.object,
  setFormState: PropTypes.func.isRequired,
  setStep: PropTypes.func.isRequired,
}

export default UserData
